import { icon } from '../shared/icons/render.js';
// /public/js/utils/connectivity.js

/**
 * Global Connectivity Monitor
 * Provides offline detection, banner display, and queue management
 */

// =================== CONNECTIVITY OVERRIDE (Welle W-ONLINE-1 Phase 0) ===================

/**
 * Returns the browser's online state, honoring two override channels:
 *   1. `window.__citaForceOnline === true` (runtime override, e.g. DevTools)
 *   2. `localStorage["cita.force_online"] === "1"` (persistent per-browser)
 *
 * Both override `navigator.onLine === false` and report `true`.
 * Default behavior (no override set) is identical to `navigator.onLine`.
 *
 * Background: Chrome 148+ on macOS with IPv6 + utun tunnels can latch
 * `navigator.onLine = false` while the network actually works (Chromium
 * NetworkChangeNotifier regression). Affected users opt-in to the override
 * via `localStorage.setItem('cita.force_online','1')` until the upstream
 * bug clears or Phase 2 SSOT migration ships. Default users are unaffected.
 *
 * See: reference_navigator_online_unreliable.md, project_w_online_1_2026_05_16.md
 *
 * @returns {boolean}
 */
export function isBrowserOnline() {
  try {
    if (typeof window !== 'undefined' && window.__citaForceOnline === true) return true;
    if (typeof localStorage !== 'undefined' && localStorage.getItem('cita.force_online') === '1') return true;
  } catch (_e) {
    // SecurityError im sandboxed iframe / Storage-disabled: fall through to navigator.onLine
  }
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

// =================== BACKEND-INTEGRATION (Phase 1) ===================

/**
 * Server-Reachability-Probe via Cita-Wrapper-Endpoint
 * (cita_events.api.common.connectivity.probe).
 *
 * Verwendet den rate-limiteten Wrapper (12/min/IP) statt direkt
 * `frappe.ping` (Sina-S1a Self-DDoS-Defense). Same-Origin only.
 * 4s-Timeout via AbortController.
 *
 * Anwendungsfall (Phase 2 SSOT-Migration): wenn `isBrowserOnline()`
 * `false` zurueckliefert, koennen Surfaces ein `serverReachable()`
 * probieren, bevor sie Offline-UI zeigen — wenn das probe erfolgreich
 * ist, war `navigator.onLine` eine Browser-Luege.
 *
 * Heute (Phase 1) noch nicht aus Phase-0-Surfaces gerufen — Helper
 * wird in Phase 2 zum Hybrid-SSOT-Pattern verdrahtet.
 *
 * @returns {Promise<boolean>} true bei 2xx-Response, false sonst
 */
export async function serverReachable() {
  try {
    const ctl = new AbortController();
    const timeoutId = setTimeout(() => ctl.abort('timeout'), 4000);
    const res = await fetch(
      '/api/method/cita_events.api.common.connectivity.probe',
      {
        method: 'GET',
        credentials: 'same-origin',
        signal: ctl.signal,
        // Kein Cache-Hit, sonst falsch-positiv
        cache: 'no-store',
      }
    );
    clearTimeout(timeoutId);
    return res.ok;
  } catch (_e) {
    // FAIL-OPEN: Probe-Fail = nicht erreichbar, kein Crash
    return false;
  }
}

/**
 * Notify backend that we detected a `navigator.onLine===false`
 * false-positive (probe succeeded but navigator lied).
 *
 * Increments backend counter `cita_offline_banner_false_positive_total
 * {surface}` via cita_events.api.common.connectivity.telemetry.
 *
 * DAUERHAFT NO-OP bis Daria-Sign-Off auf DSGVO-Pflicht-Nachzieher
 * P1-1 (Datenschutz-Passus + Re-Consent), P1-2 (Art.30-Eintrag),
 * P1-3 (Retention-Policy). Killswitch via globalem Flag:
 *
 *   window.__citaConnectivityTelemetryEnabled = true;
 *
 * Solange das Flag NICHT explizit auf true gesetzt ist, returnt der
 * Helper SOFORT ohne fetch-Aufruf — Zero-Backend-Hit + Zero-PII.
 *
 * Auch nach Aktivierung ist der Helper FAIL-OPEN: jeder fetch-Fehler
 * wird stillschweigend geschluckt; kein User-Impact, kein Crash.
 *
 * @param {string} surface — must be in backend KNOWN_SURFACES allowlist
 *   (ticket_view, navbar_profile_load, publish_v2_store, ...).
 *   Ungueltige surface -> Backend antwortet json_err("invalid_surface").
 * @returns {Promise<void>}
 */
export async function notifyFalsePositive(surface) {
  if (typeof window === 'undefined') return;
  if (window.__citaConnectivityTelemetryEnabled !== true) {
    // No-op bis Daria-Sign-Off. Siehe DSGVO-Spec:
    // ~/.claude/plans/W-ONLINE-1-DSGVO-Spec-2026-05-16.md
    return;
  }
  if (!surface || typeof surface !== 'string') return;
  try {
    const csrfToken =
      window.csrf_token ||
      (typeof document !== 'undefined' &&
        document.querySelector('meta[name="csrf_token"]')?.content) ||
      '';
    await fetch(
      '/api/method/cita_events.api.common.connectivity.telemetry',
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Frappe-CSRF-Token': csrfToken,
        },
        body: `surface=${encodeURIComponent(surface)}`,
        cache: 'no-store',
      }
    );
  } catch (_e) {
    // FAIL-OPEN: Telemetry-Fail darf User nicht beeinflussen
  }
}

// =================== CONNECTIVITY STATE ===================

let isOnline = navigator.onLine;
let banner = null;
let eventQueue = [];
const BANNER_ID = 'cita-connectivity-banner';

// =================== BANNER MANAGEMENT ===================

/**
 * Create offline banner element
 * @returns {HTMLElement}
 */
const createBanner = () => {
  if (document.getElementById(BANNER_ID)) {
    return document.getElementById(BANNER_ID);
  }

  const el = document.createElement('div');
  el.id = BANNER_ID;
  el.className = 'connectivity-banner';
  el.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10000;
    padding: 12px 16px;
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    color: white;
    font-weight: 600;
    font-size: 14px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transform: translateY(-100%);
    transition: transform 0.3s ease-out;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  `;

  el.innerHTML = `
    ${icon('wifiOff')}
    <span class="connectivity-message">Offline – Änderungen werden lokal gespeichert</span>
  `;

  document.body.appendChild(el);
  return el;
};

/**
 * Show offline banner
 * @param {string} message - Custom message
 */
export const showOfflineBanner = (message = 'Offline – Änderungen werden lokal gespeichert') => {
  if (!banner) {
    banner = createBanner();
  }

  const messageEl = banner.querySelector('.connectivity-message');
  if (messageEl) {
    messageEl.textContent = message;
  }

  // Force reflow before animation
  void banner.offsetWidth;
  banner.style.transform = 'translateY(0)';
};

/**
 * Hide offline banner
 */
export const hideOfflineBanner = () => {
  if (!banner) return;
  banner.style.transform = 'translateY(-100%)';
};

// =================== QUEUE MANAGEMENT ===================

/**
 * Queue an action for when we're back online
 * @param {Function} action - Action to queue
 * @param {Object} meta - Optional metadata for the action
 */
export const queueForOnline = (action, meta = {}) => {
  eventQueue.push({ action, meta, timestamp: Date.now() });
};

/**
 * Flush queued actions
 * @returns {Promise<void>}
 */
export const flushQueue = async () => {
  if (eventQueue.length === 0) return;

  const toFlush = [...eventQueue];
  eventQueue = [];

  for (const item of toFlush) {
    try {
      await item.action();
    } catch (error) {
      console.warn('[Connectivity] Failed to flush queued action:', error);
      // Re-queue failed actions
      eventQueue.push(item);
    }
  }
};

/**
 * Get current queue length
 * @returns {number}
 */
export const getQueueLength = () => eventQueue.length;

/**
 * Clear queue without executing
 */
export const clearQueue = () => {
  eventQueue = [];
};

// =================== EVENT HANDLERS ===================

/**
 * Handle online event
 */
const handleOnline = () => {
  isOnline = true;
  hideOfflineBanner();

  // Flush queued actions
  flushQueue().catch(err => {
    console.error('[Connectivity] Error flushing queue:', err);
  });

  // Dispatch custom event for app-level handling
  window.dispatchEvent(new CustomEvent('cita:online', {
    detail: { queueLength: getQueueLength() }
  }));
};

/**
 * Handle offline event
 */
const handleOffline = () => {
  isOnline = false;
  showOfflineBanner();

  // Dispatch custom event for app-level handling
  window.dispatchEvent(new CustomEvent('cita:offline'));
};

// =================== CONNECTIVITY MONITOR ===================

/**
 * Initialize connectivity monitoring
 * Call once at app startup
 */
export const initConnectivityMonitor = () => {
  // Set initial state
  isOnline = navigator.onLine;

  // Register event listeners
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Show banner if starting offline
  if (!isOnline) {
    showOfflineBanner();
  }

  return {
    destroy: () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (banner) {
        banner.remove();
        banner = null;
      }
    }
  };
};

/**
 * Check if currently online
 * @returns {boolean}
 */
export const checkOnline = () => isOnline;

/**
 * Wait for online status
 * @param {number} timeout - Optional timeout in ms
 * @returns {Promise<boolean>}
 */
export const waitForOnline = (timeout = 0) => {
  if (isOnline) return Promise.resolve(true);

  return new Promise((resolve) => {
    const handler = () => {
      window.removeEventListener('online', handler);
      if (timer) clearTimeout(timer);
      resolve(true);
    };

    let timer = null;
    if (timeout > 0) {
      timer = setTimeout(() => {
        window.removeEventListener('online', handler);
        resolve(false);
      }, timeout);
    }

    window.addEventListener('online', handler);
  });
};

// =================== EXPORT ===================

export default {
  initConnectivityMonitor,
  showOfflineBanner,
  hideOfflineBanner,
  queueForOnline,
  flushQueue,
  getQueueLength,
  clearQueue,
  checkOnline,
  waitForOnline,
};
